import React, { useEffect, useRef, useState } from 'react';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import toast from 'react-hot-toast';
import { auth } from '../firebase';

const RESEND_COOLDOWN = 30; // seconds

// Reusable Firebase phone-OTP widget. Mirrors the send-OTP/verify-OTP flow used
// for teacher registration: invisible reCAPTCHA -> signInWithPhoneNumber -> confirm(code)
// -> getIdToken(). The parent owns "is this phone verified" (via the `verified` prop) and
// must clear it whenever the phone number changes, so a verified idToken can never be
// carried over to a different phone number than the one it was issued for.
export default function PhoneOtpVerify({ phone, verified, onVerified, disabled }) {
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const confirmationRef = useRef(null);
  const verifierRef = useRef(null);
  const recaptchaDivId = useRef(`recaptcha-${Math.random().toString(36).slice(2)}`).current;

  // A phone edit invalidates any in-flight OTP session for the old number.
  useEffect(() => {
    setSent(false);
    setCode('');
    confirmationRef.current = null;
  }, [phone]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  useEffect(() => () => { try { verifierRef.current?.clear(); } catch {} }, []);

  const getVerifier = () => {
    if (!verifierRef.current) {
      verifierRef.current = new RecaptchaVerifier(auth, recaptchaDivId, { size: 'invisible' });
    }
    return verifierRef.current;
  };

  const sendOtp = async () => {
    if (!/^[0-9]{10}$/.test(phone)) return toast.error('Enter a valid 10-digit phone number first');
    setSending(true);
    try {
      const verifier = getVerifier();
      confirmationRef.current = await signInWithPhoneNumber(auth, '+91' + phone, verifier);
      setSent(true);
      setCooldown(RESEND_COOLDOWN);
      toast.success(`OTP sent to +91 ${phone}`);
    } catch (err) {
      try { verifierRef.current?.clear(); } catch {}
      verifierRef.current = null;
      console.error('OTP send error:', err);
      // TEMP DEBUG: surfacing the raw Firebase error so it can be read on a phone with no
      // console access. Revert to the generic "Failed to send OTP" message once diagnosed.
      toast.error(`${err.code || 'unknown'}: ${err.message || err}`, { duration: 15000 });
    } finally { setSending(false); }
  };

  const verifyOtp = async () => {
    if (!code.trim()) return toast.error('Enter the OTP code');
    if (!confirmationRef.current) return toast.error('Please request an OTP first');
    setVerifying(true);
    try {
      const cred = await confirmationRef.current.confirm(code.trim());
      const idToken = await cred.user.getIdToken();
      onVerified(idToken);
      toast.success('Phone number verified!');
    } catch (err) {
      toast.error('Incorrect or expired OTP. Please try again.');
    } finally { setVerifying(false); }
  };

  if (verified) {
    return (
      <div className="alert alert-success" style={{ fontSize: 13, marginBottom: 16 }}>
        ✅ Phone number verified
      </div>
    );
  }

  return (
    <div className="form-group">
      <div id={recaptchaDivId} />
      <label>Verify Phone Number *</label>
      {!sent ? (
        <button type="button" className="btn btn-secondary btn-full" disabled={disabled || sending} onClick={sendOtp}>
          {sending ? '⏳ Sending OTP...' : '📲 Send OTP'}
        </button>
      ) : (
        <div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter OTP"
              inputMode="numeric"
              maxLength={6}
            />
            <button type="button" className="btn btn-primary" disabled={disabled || verifying} onClick={verifyOtp} style={{ whiteSpace: 'nowrap' }}>
              {verifying ? '⏳ Verifying...' : '✅ Verify'}
            </button>
          </div>
          <button
            type="button"
            onClick={sendOtp}
            disabled={disabled || sending || cooldown > 0}
            style={{ background: 'none', border: 'none', color: 'var(--primary-light)', fontSize: 12, cursor: cooldown > 0 ? 'default' : 'pointer', marginTop: 8, padding: 0 }}
          >
            {cooldown > 0 ? `Resend OTP in ${cooldown}s` : 'Resend OTP'}
          </button>
        </div>
      )}
    </div>
  );
}
